import { FormGroup } from "reactstrap";
import { AppToggle } from "../Fields/CustomToggle";
import { LayoutInfo } from "./SectionsAndLayoutsDrawer";
import { Dropdown, SelectPicker } from "rsuite";
import NewButton from "../../../elements/NewButton";
import { useRef } from "react";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import StrUtils from "../../../utils/StrUtils";
import CollapseItem from "../../FormBuilder/components/CollapseItem";
import { Field, Module } from "../../../types/TenantInterfaces";

interface BaseShareOption {}

interface EmailShareOption extends BaseShareOption {
  id: string;
  Name?: string;
  Type: "Email";
  FieldMap: EmailFieldMap;
}

export interface EmailFieldMap {
  Subject?: string;
  Body?: string;
  To?: string;
  CC?: string;
  BCC?: string;
}

export type ShareOptions = EmailShareOption;

interface Props {
  layoutInfo: LayoutInfo;
  setLayoutInfo: React.Dispatch<React.SetStateAction<LayoutInfo>>;
  currentModule: Module;
}

export default function ShareButtonConfig({
  layoutInfo,
  setLayoutInfo,
  currentModule,
}: Props) {
  const shareOptions = layoutInfo?.ShareOptions;
  const buttonRef = useRef<any>();

  return (
    <FormGroup className="mt-3">
      <div className="mb-2">
        <h5 className="mb-0 text-muted">Share Button</h5>
        <small>Configure share options for this layout</small>
      </div>
      <AppToggle
        value={!!shareOptions}
        handleChange={(e) => {
          setLayoutInfo((prev) => ({
            ...prev,
            ShareOptions: e.target.checked ? [] : null,
          }));
        }}
      />
      {shareOptions && (
        <div className="border rounded p-2">
          <NewButton
            title="Share Options"
            onClick={() => {}}
            newButtonRef={buttonRef}
            buttonGroupCSS={"mb-4"}
            options={
              <Options
                layoutInfo={layoutInfo}
                setLayoutInfo={setLayoutInfo}
                buttonRef={buttonRef}
              />
            }
            onlyOptions={true}
          />
          <div className="mt-2">
            {shareOptions?.length > 0 &&
              shareOptions.map((option) => {
                return (
                  <CollapseItem
                    key={option.id}
                    title={option.Type}
                    onDelete={() =>
                      setLayoutInfo((prev) => ({
                        ...prev,
                        ShareOptions: prev?.ShareOptions?.filter(
                          (o) => o.id !== option.id
                        ),
                      }))
                    }
                  >
                    <ShareOptionConfig
                      option={option}
                      fields={currentModule?.Fields}
                      setLayoutInfo={setLayoutInfo}
                    />
                  </CollapseItem>
                );
              })}
          </div>
        </div>
      )}
    </FormGroup>
  );
}

function Options({
  layoutInfo,
  setLayoutInfo,
  buttonRef,
}: {
  layoutInfo: LayoutInfo;
  setLayoutInfo: React.Dispatch<React.SetStateAction<LayoutInfo>>;
  buttonRef: any;
}) {
  return (
    <Dropdown.Menu style={{ maxWidth: 250 }}>
      <DropdownOptionWithIcon
        key={1}
        name={`Add Email Option`}
        onClick={() => {
          if (layoutInfo?.ShareOptions?.find((o) => o.Type === "Email")) {
            return buttonRef.current?.close?.();
          }

          setLayoutInfo((prev) => ({
            ...prev,
            ShareOptions: [
              ...(prev?.ShareOptions || []),
              {
                id: StrUtils.uuidv4(),
                Type: "Email",
                FieldMap: { Subject: "", Body: "" },
              },
            ],
          }));
          buttonRef.current?.close?.();
        }}
        icon={"mdi mdi-email"}
      />
    </Dropdown.Menu>
  );
}

const textTypes = ["Text", "LongText", "RichText"];

function ShareOptionConfig({
  option,
  fields,
  setLayoutInfo,
}: {
  option: ShareOptions;
  fields: Field[];
  setLayoutInfo: React.Dispatch<React.SetStateAction<LayoutInfo>>;
}) {
  if (option.Type === "Email") {
    return (
      <>
        {Object.entries(option.FieldMap).map(([field, value]) => {
          return (
            <div className="d-flex align-items-center mb-3" key={field}>
              <h4 className="mb-0" style={{ width: 200 }}>
                Email {field}
              </h4>
              <SelectPicker
                value={value || null}
                data={
                  fields?.filter((f) => textTypes.includes(f.FieldType)) || []
                }
                onChange={(val) => {
                  setLayoutInfo((prev) => ({
                    ...prev,
                    ShareOptions: prev.ShareOptions?.map((o) => {
                      if (o.id !== option.id) return o;

                      o.FieldMap[field as keyof EmailFieldMap] = val;

                      return o;
                    }),
                  }));
                }}
                labelKey="Name"
                valueKey="APIName"
                className="w-100"
                placeholder="Select Field"
              />
            </div>
          );
        })}
      </>
    );
  }

  return null;
}
